import { createElement } from 'react';
import { formatBRL } from '@/lib/format';
import { InstituicaoLogo } from './components/InstituicaoLogo';
import {
  asRaw,
  asStr,
  buildObservacoes,
  buildPhases,
  fmtDate,
  parseResponsaveis,
  statusLabel,
  statusTone,
  type CardFieldMap,
  type DetailFieldMap,
  type PipeSpec,
} from '@/modules/kanban';
import { FINALIDADE_LABEL } from './data';
import { formatCardTime } from './card-times';

function fmtBRLOrNull(v: unknown): string | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return null;
  return formatBRL(n);
}

function normalizeProgramaLinha(v: unknown): string | null {
  if (v == null || v === '') return null;
  return String(v).replace(/\s+ASTEC$/i, '').trim();
}

const FINALIDADE = [
  { value: 'custeio', label: 'Custeio' },
  { value: 'investimento', label: 'Investimento' },
  { value: 'comercializacao', label: 'Comercialização' },
  { value: 'industrializacao', label: 'Industrialização' },
];
const TRIAGEM_INSTITUICOES = [
  { value: '2', label: 'Banco da Amazônia (BASA)' },
  { value: '4', label: 'Banco do Brasil' },
  { value: '1', label: 'Sicoob Credisul' },
  { value: '12', label: 'Sicoob Fronteiras' },
  { value: '8', label: 'Sicredi' },
  { value: '5', label: 'Banco Bradesco' },
];
const PORTE = [
  { value: 'micro', label: 'Micro' },
  { value: 'pequeno', label: 'Pequeno' },
  { value: 'medio', label: 'Médio' },
  { value: 'grande', label: 'Grande' },
  { value: 'mega', label: 'Mega' },
];
const PARECER_IF = [
  { value: 'favoravel', label: 'Favorável' },
  { value: 'desfavoravel', label: 'Desfavorável' },
  { value: 'condicional', label: 'Condicional' },
];
const TIPO_GARANTIA = [
  { value: 'hipoteca', label: 'Hipoteca' },
  { value: 'penhor', label: 'Penhor' },
  { value: 'aval', label: 'Aval' },
  { value: 'nota_promissoria', label: 'Nota promissória' },
];
const LICENCA_TIPO = [
  { value: 'lp', label: 'LP — Licença prévia' },
  { value: 'li', label: 'LI — Licença de instalação' },
  { value: 'lo', label: 'LO — Licença de operação' },
  { value: 'sem_licenca', label: 'Sem licença' },
];
const SIM_NAO_TRAMITE = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
  { value: 'em_tramite', label: 'Em trâmite' },
];
const SIM_NAO_BOOLEANO = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

const PIPE_TRIAGEM_CREDITO: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Entrada',
      fields: [
        { key: 'municipio', label: 'Município', type: 'text' },
        { key: 'data_entrada', label: 'Data de entrada', type: 'date' },
      ],
    },
    {
      name: 'Análise',
      fields: [
        { key: 'observacoes', label: 'Triagem documental e operacional', type: 'textarea' },
        { key: 'responsavel_id', label: 'Assistente sugerida', type: 'triagem_aceite' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
      ],
    },
    {
      name: 'Definição',
      fields: [
        { key: 'finalidade_codigo', label: 'Finalidade', type: 'select', options: FINALIDADE },
        { key: 'instituicao_id', label: 'Instituição financeira', type: 'select', options: TRIAGEM_INSTITUICOES },
        { key: 'prazo_proxima_acao', label: 'Prazo da definição', type: 'date' },
      ],
    },
    {
      name: 'Encaminhado',
      fields: [
        { key: 'instituicao_nome', label: 'Instituição definida', type: 'text', editable: false },
        { key: 'programa_nome', label: 'Linha/programa definido', type: 'text', editable: false },
        { key: 'observacoes_finais', label: 'Registro do encaminhamento', type: 'textarea' },
      ],
    },
  ],
};

const PIPE_COMPROVACOES: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Liberação inicial',
      fields: [
        { key: 'numero_cedula', label: 'Número da cédula/operação', type: 'text' },
        { key: 'valor_aprovado', label: 'Valor total da operação (R$)', type: 'number' },
        {
          key: 'recurso_creditado_cliente',
          label: 'O recurso da operação já foi efetivamente creditado na conta do cliente?',
          type: 'select',
          options: SIM_NAO_BOOLEANO,
        },
        { key: 'data_prevista_envio_laudo', label: 'Data prevista para enviar o laudo', type: 'date' },
      ],
    },
    {
      name: 'Liberação programada',
      fields: [
        { key: 'data_prevista_liberacao', label: 'Data prevista da liberação', type: 'date' },
        { key: 'valor_liberacao_programada', label: 'Valor programado da parcela (R$)', type: 'number' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo da próxima ação', type: 'date' },
      ],
    },
    {
      name: 'Laudo de comprovação',
      fields: [
        { key: 'data_prevista_envio_laudo', label: 'Data prevista para enviar o laudo', type: 'date' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo da próxima ação', type: 'date' },
      ],
    },
    {
      name: 'Recurso liberado',
      fields: [
        { key: 'data_prevista_liberacao', label: 'Data prevista/efetiva da liberação', type: 'date' },
        { key: 'observacoes_finais', label: 'Comprovante e observações da liberação', type: 'textarea' },
      ],
    },
    {
      name: 'Liberação indeferida',
      fields: [
        { key: 'motivo_perda_cancelamento', label: 'Motivo do indeferimento', type: 'textarea' },
        { key: 'observacoes_finais', label: 'Providências e observações', type: 'textarea' },
      ],
    },
  ],
};

// Pipe FNO/BASA — execução após a triagem. Outros pipes institucionais
// reutilizam essa base quando não têm spec própria.
const PIPE_CREDITO: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Triagem',
      fields: [
        { key: 'municipio', label: 'Município', type: 'text' },
        { key: 'data_entrada', label: 'Data de entrada', type: 'date' },
      ],
    },
    {
      name: 'Cadastro conferido',
      fields: [
        { key: 'observacoes', label: 'Observações do cadastro', type: 'textarea' },
      ],
    },
    {
      name: 'Imóvel / área vinculada',
      fields: [
        { key: 'area_estimada_ha', label: 'Área estimada (ha)', type: 'number' },
      ],
    },
    {
      name: 'Terras crédito',
      fields: [
        { key: 'observacoes', label: 'Status Terras Crédito', type: 'textarea' },
      ],
    },
    {
      name: 'Enquadramento FNO',
      fields: [
        { key: 'finalidade_codigo', label: 'Finalidade', type: 'select', options: FINALIDADE },
        { key: 'porte_codigo', label: 'Porte', type: 'select', options: PORTE },
      ],
    },
    {
      name: 'Documentação em coleta',
      fields: [
        { key: 'observacoes', label: 'Observações sobre documentação', type: 'textarea' },
        { key: 'numero_protocolo', label: 'Protocolo da documentação', type: 'text' },
      ],
    },
    {
      name: 'Projeto / orçamento',
      fields: [
        { key: 'cultura_atividade', label: 'Cultura/atividade', type: 'text' },
        { key: 'area_financiada_ha', label: 'Área a financiar (ha)', type: 'number' },
        { key: 'valor_solicitado', label: 'Valor solicitado (R$)', type: 'number' },
      ],
    },
    {
      name: 'Análise interna',
      fields: [
        { key: 'valor_aprovado', label: 'Valor aprovado (R$)', type: 'number' },
        { key: 'taxa_juros_aa', label: 'Taxa de juros a.a. (%)', type: 'number' },
        { key: 'percentual_financiavel', label: 'Percentual financiável (%)', type: 'number' },
        { key: 'observacoes', label: 'Análise interna', type: 'textarea' },
      ],
    },
    {
      name: 'Instituição',
      fields: [
        { key: 'parecer_if', label: 'Parecer da IF', type: 'select', options: PARECER_IF },
        { key: 'data_parecer_if', label: 'Data do parecer IF', type: 'date' },
        { key: 'if_substatus', label: 'Sub-status IF', type: 'text' },
      ],
    },
    {
      name: 'Aprovado / em contratação',
      fields: [
        { key: 'numero_cedula', label: 'Nº cédula', type: 'text' },
        { key: 'prazo_total_meses', label: 'Prazo total (meses)', type: 'number' },
        { key: 'carencia_meses', label: 'Carência (meses)', type: 'number' },
        { key: 'juros_na_carencia', label: 'Cobra juros na carência?', type: 'boolean' },
        { key: 'bonus_adimplencia', label: 'Bônus de adimplência (%)', type: 'number' },
      ],
    },
    {
      name: 'Contratado',
      fields: [
        { key: 'data_assinatura_cedula', label: 'Data de assinatura', type: 'date' },
        { key: 'data_primeiro_vencimento', label: 'Data 1º vencimento', type: 'date' },
        { key: 'tipo_garantia_principal_codigo', label: 'Tipo garantia principal', type: 'select', options: TIPO_GARANTIA },
      ],
    },
    {
      name: 'Pós-contratação',
      fields: [
        { key: 'proagro_obrigatorio', label: 'PROAGRO obrigatório?', type: 'boolean' },
        { key: 'proagro_aliquota_financiada', label: 'Alíquota PROAGRO (%)', type: 'number' },
        { key: 'licenca_ambiental_tipo', label: 'Tipo de licença ambiental', type: 'select', options: LICENCA_TIPO },
        { key: 'licenca_ambiental_validade', label: 'Validade da licença', type: 'date' },
        { key: 'outorga_agua_status', label: 'Outorga de água?', type: 'select', options: SIM_NAO_TRAMITE },
        { key: 'zee_aderente', label: 'Aderente ao ZEE?', type: 'boolean' },
        { key: 'zarc_aderente', label: 'Aderente ao ZARC?', type: 'boolean' },
      ],
    },
    {
      name: 'Concluído',
      fields: [
        { key: 'observacoes_finais', label: 'Observações finais', type: 'textarea' },
        { key: 'proxima_acao', label: 'Próxima ação recomendada', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo da próxima ação', type: 'date' },
      ],
    },
    {
      name: 'Perdido',
      fields: [
        { key: 'motivo_perda_cancelamento', label: 'Motivo da perda/cancelamento', type: 'text' },
        { key: 'observacoes_finais', label: 'Observações finais', type: 'textarea' },
      ],
    },
  ],
};


const PIPE_SICOOB_FRONTEIRAS: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Entrada',
      fields: [
        { key: 'municipio', label: 'Município', type: 'text' },
        { key: 'data_entrada', label: 'Data de entrada', type: 'date' },
        { key: 'finalidade_codigo', label: 'Finalidade', type: 'select', options: FINALIDADE },
        { key: 'cultura_atividade', label: 'Cultura/atividade', type: 'text' },
        { key: 'valor_solicitado', label: 'Valor solicitado (R$)', type: 'number' },
      ],
    },
    {
      name: 'Enquadramento',
      fields: [
        { key: 'programa_nome', label: 'Linha/programa', type: 'text' },
        { key: 'finalidade_codigo', label: 'Finalidade', type: 'select', options: FINALIDADE },
        { key: 'porte_codigo', label: 'Porte', type: 'select', options: PORTE },
        { key: 'area_financiada_ha', label: 'Área a financiar (ha)', type: 'number' },
        { key: 'tipo_garantia_principal_codigo', label: 'Garantia disponível', type: 'select', options: TIPO_GARANTIA },
        { key: 'observacoes', label: 'Análise preliminar e restrições', type: 'textarea' },
      ],
    },
    {
      name: 'Documentação',
      fields: [
        { key: 'numero_protocolo', label: 'Protocolo da documentação', type: 'text' },
        { key: 'observacoes', label: 'Pendências e conferência documental', type: 'textarea' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo da próxima ação', type: 'date' },
      ],
    },
    {
      name: 'Projeto / Dossiê',
      fields: [
        { key: 'cultura_atividade', label: 'Cultura/atividade', type: 'text' },
        { key: 'area_financiada_ha', label: 'Área a financiar (ha)', type: 'number' },
        { key: 'valor_solicitado', label: 'Valor solicitado (R$)', type: 'number' },
        { key: 'prazo_total_meses', label: 'Prazo total (meses)', type: 'number' },
        { key: 'carencia_meses', label: 'Carência (meses)', type: 'number' },
        { key: 'tipo_garantia_principal_codigo', label: 'Garantia principal', type: 'select', options: TIPO_GARANTIA },
        { key: 'observacoes', label: 'Orçamento, cronograma e parecer técnico', type: 'textarea' },
      ],
    },
    {
      name: 'Análise Sicoob',
      fields: [
        { key: 'numero_protocolo', label: 'Protocolo no Sicoob', type: 'text' },
        { key: 'if_substatus', label: 'Status no Sicoob', type: 'text' },
        { key: 'data_substatus', label: 'Data do status', type: 'date' },
        { key: 'parecer_if', label: 'Parecer do Sicoob', type: 'select', options: PARECER_IF },
        { key: 'data_parecer_if', label: 'Data do parecer', type: 'date' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo de acompanhamento', type: 'date' },
      ],
    },
    {
      name: 'Ajustes',
      fields: [
        { key: 'observacoes', label: 'Devolutiva e ajustes solicitados', type: 'textarea' },
        { key: 'proxima_acao', label: 'Correção necessária', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo para correção', type: 'date' },
        { key: 'data_substatus', label: 'Data do reenvio', type: 'date' },
      ],
    },
    {
      name: 'Aprovado',
      fields: [
        { key: 'parecer_if', label: 'Parecer do Sicoob', type: 'select', options: PARECER_IF },
        { key: 'data_parecer_if', label: 'Data da aprovação', type: 'date' },
        { key: 'valor_aprovado', label: 'Valor aprovado (R$)', type: 'number' },
        { key: 'taxa_juros_aa', label: 'Taxa de juros a.a. (%)', type: 'number' },
        { key: 'prazo_total_meses', label: 'Prazo total (meses)', type: 'number' },
        { key: 'carencia_meses', label: 'Carência (meses)', type: 'number' },
        { key: 'numero_cedula', label: 'Nº cédula/contrato', type: 'text' },
        { key: 'observacoes_finais', label: 'Condicionantes, contratação e liberação', type: 'textarea' },
      ],
    },
    {
      name: 'Indeferido',
      fields: [
        { key: 'parecer_if', label: 'Parecer do Sicoob', type: 'select', options: PARECER_IF },
        { key: 'data_parecer_if', label: 'Data do indeferimento', type: 'date' },
        { key: 'motivo_perda_cancelamento', label: 'Motivo do indeferimento', type: 'text' },
        { key: 'observacoes_finais', label: 'Devolutiva e orientação final', type: 'textarea' },
      ],
    },
  ],
};

const PIPE_BRADESCO: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Cadastro Rural',
      fields: [
        { key: 'municipio', label: 'Município do processo', type: 'text' },
        { key: 'cultura_atividade', label: 'Atividade principal', type: 'text' },
        { key: 'area_financiada_ha', label: 'Área beneficiada consolidada (ha)', type: 'number' },
        { key: 'observacoes', label: 'Imóveis beneficiados, CAR, roteiro, rebanho e pendências de cadastro', type: 'textarea' },
      ],
    },
    {
      name: 'Proposta de Crédito',
      fields: [
        { key: 'finalidade_codigo', label: 'Finalidade', type: 'select', options: FINALIDADE },
        { key: 'valor_solicitado', label: 'Valor solicitado (R$)', type: 'number' },
        { key: 'taxa_juros_aa', label: 'Taxa de juros a.a. (%)', type: 'number' },
        { key: 'prazo_total_meses', label: 'Prazo total (meses)', type: 'number' },
        { key: 'carencia_meses', label: 'Carência (meses)', type: 'number' },
        { key: 'tipo_garantia_principal_codigo', label: 'Garantia principal', type: 'select', options: TIPO_GARANTIA },
        { key: 'observacoes', label: 'Orçamento, renda, fluxo de caixa e garantias', type: 'textarea' },
      ],
    },
    {
      name: 'Projeto Bradesco',
      fields: [
        { key: 'proagro_obrigatorio', label: 'Declaração SICOR/PROAGRO revisada?', type: 'boolean' },
        { key: 'zarc_aderente', label: 'ZARC/atividade aderente, quando aplicável?', type: 'boolean' },
        { key: 'licenca_ambiental_tipo', label: 'Situação/licença ambiental', type: 'select', options: LICENCA_TIPO },
        { key: 'observacoes', label: 'DOCX/PDF, coordenadas, mapa, comentário técnico e validação humana', type: 'textarea' },
      ],
    },
    {
      name: 'Esteira Bradesco',
      fields: [
        { key: 'if_substatus', label: 'Status no Bradesco', type: 'text' },
        { key: 'data_substatus', label: 'Data do status', type: 'date' },
        { key: 'parecer_if', label: 'Parecer do banco', type: 'select', options: PARECER_IF },
        { key: 'valor_aprovado', label: 'Valor aprovado (R$)', type: 'number' },
        { key: 'numero_cedula', label: 'Nº cédula/contrato', type: 'text' },
        { key: 'data_assinatura_cedula', label: 'Data de contratação', type: 'date' },
        { key: 'data_primeiro_vencimento', label: 'Data 1º vencimento', type: 'date' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo da próxima ação', type: 'date' },
      ],
    },
  ],
};

const PIPE_CREDISIS: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Cadastro Rural',
      fields: [
        { key: 'municipio', label: 'Município do processo', type: 'text' },
        { key: 'cultura_atividade', label: 'Atividade principal', type: 'text' },
        { key: 'area_financiada_ha', label: 'Área beneficiada consolidada (ha)', type: 'number' },
        { key: 'observacoes', label: 'Imóveis beneficiados, CAR, roteiro, rebanho e pendências de cadastro', type: 'textarea' },
      ],
    },
    {
      name: 'Proposta de Crédito',
      fields: [
        { key: 'finalidade_codigo', label: 'Finalidade', type: 'select', options: FINALIDADE },
        { key: 'valor_solicitado', label: 'Valor solicitado (R$)', type: 'number' },
        { key: 'taxa_juros_aa', label: 'Taxa de juros a.a. (%)', type: 'number' },
        { key: 'prazo_total_meses', label: 'Prazo total (meses)', type: 'number' },
        { key: 'carencia_meses', label: 'Carência (meses)', type: 'number' },
        { key: 'tipo_garantia_principal_codigo', label: 'Garantia principal', type: 'select', options: TIPO_GARANTIA },
        { key: 'observacoes', label: 'Orçamento, renda, fluxo de caixa e garantias', type: 'textarea' },
      ],
    },
    {
      name: 'Projeto Credisis',
      fields: [
        { key: 'proagro_obrigatorio', label: 'Declaração SICOR/PROAGRO revisada?', type: 'boolean' },
        { key: 'zarc_aderente', label: 'ZARC/atividade aderente, quando aplicável?', type: 'boolean' },
        { key: 'licenca_ambiental_tipo', label: 'Situação/licença ambiental', type: 'select', options: LICENCA_TIPO },
        { key: 'observacoes', label: 'DOCX/PDF, coordenadas, mapa, comentário técnico e validação humana', type: 'textarea' },
      ],
    },
    {
      name: 'Esteira Credisis',
      fields: [
        { key: 'if_substatus', label: 'Status no Credisis', type: 'text' },
        { key: 'data_substatus', label: 'Data do status', type: 'date' },
        { key: 'parecer_if', label: 'Parecer da cooperativa', type: 'select', options: PARECER_IF },
        { key: 'valor_aprovado', label: 'Valor aprovado (R$)', type: 'number' },
        { key: 'numero_cedula', label: 'Nº cédula/contrato', type: 'text' },
        { key: 'data_assinatura_cedula', label: 'Data de contratação', type: 'date' },
        { key: 'data_primeiro_vencimento', label: 'Data 1º vencimento', type: 'date' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo da próxima ação', type: 'date' },
      ],
    },
  ],
};

const PIPE_SICREDI: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Cadastro Sicredi',
      fields: [
        { key: 'municipio', label: 'Município do processo', type: 'text' },
        { key: 'cultura_atividade', label: 'Atividade principal', type: 'text' },
        { key: 'area_financiada_ha', label: 'Área beneficiada (ha)', type: 'number' },
        { key: 'observacoes', label: 'Cadastro do cliente, imóveis, documentos-base e pendências iniciais', type: 'textarea' },
      ],
    },
    {
      name: 'Geo Crédito / Croqui',
      fields: [
        { key: 'area_financiada_ha', label: 'Área validada no croqui (ha)', type: 'number' },
        { key: 'zarc_aderente', label: 'ZARC aderente, quando aplicável?', type: 'boolean' },
        { key: 'outorga_agua_status', label: 'Situação de outorga/água', type: 'select', options: SIM_NAO_TRAMITE },
        { key: 'observacoes', label: 'KML, Geo Crédito, croqui, CAR/CCIR/CIB e validações geoespaciais', type: 'textarea' },
      ],
    },
    {
      name: 'Protec Agro / Projeto Técnico',
      fields: [
        { key: 'finalidade_codigo', label: 'Finalidade', type: 'select', options: FINALIDADE },
        { key: 'valor_solicitado', label: 'Valor solicitado (R$)', type: 'number' },
        { key: 'prazo_total_meses', label: 'Prazo total (meses)', type: 'number' },
        { key: 'carencia_meses', label: 'Carência (meses)', type: 'number' },
        { key: 'tipo_garantia_principal_codigo', label: 'Garantia principal', type: 'select', options: TIPO_GARANTIA },
        { key: 'observacoes', label: 'Protec Agro, orçamento, produção, garantias e parecer técnico', type: 'textarea' },
      ],
    },
    {
      name: 'Esteira Sicredi',
      fields: [
        { key: 'if_substatus', label: 'Status no Sicredi', type: 'text' },
        { key: 'data_substatus', label: 'Data do status', type: 'date' },
        { key: 'parecer_if', label: 'Parecer da cooperativa', type: 'select', options: PARECER_IF },
        { key: 'valor_aprovado', label: 'Valor aprovado (R$)', type: 'number' },
        { key: 'numero_cedula', label: 'Nº cédula/contrato', type: 'text' },
        { key: 'proxima_acao', label: 'Próxima ação', type: 'text' },
        { key: 'prazo_proxima_acao', label: 'Prazo da próxima ação', type: 'date' },
      ],
    },
  ],
};

function fmtFinalidade(code: unknown): string | null {
  if (!code) return null;
  return FINALIDADE_LABEL[String(code)] ?? String(code);
}

function fmtSugestaoInstituicao(raw: unknown): string | null {
  const value = raw ? String(raw).trim() : '';
  if (!value) return null;
  if (/banco da amaz[oô]nia|basa/i.test(value)) return 'BASA';
  if (/banco do brasil/i.test(value)) return 'Banco do Brasil';
  if (/sicredi/i.test(value)) return 'Sicredi';
  return value;
}

function fmtFaseTriagem(raw: unknown): string | null {
  switch (String(raw || '').trim().toLowerCase()) {
    case 'entrada':
      return 'Entrada';
    case 'analise':
    case 'análise':
      return 'Análise';
    case 'definicao':
    case 'definição':
      return 'Definição';
    case 'encaminhado':
      return 'Encaminhado';
    case 'concluido':
    case 'concluído':
      return 'Concluído';
    case 'arquivado':
      return 'Arquivado';
    default:
      return null;
  }
}

function buildPendenciasCadastrais(c: Record<string, unknown>): string[] {
  const pendencias: string[] = [];
  if (!Number(c.responsavel_id || 0) && !String(c.responsavel_travado || '').trim() && (!Array.isArray(c.responsaveis) || c.responsaveis.length === 0)) {
    pendencias.push('responsável pela operação não definido');
  }
  if (c.cliente_renda_bruta_anual == null || c.cliente_renda_bruta_anual === '') {
    pendencias.push('renda bruta anual pendente');
  }
  if (!String(c.data_previsao_conclusao || '').trim()) {
    pendencias.push('previsão de conclusão pendente');
  }
  return pendencias;
}

function checklistLabel(status: string): string {
  switch (status) {
    case 'recebido':
      return 'recebido';
    case 'pendente':
      return 'pendente';
    case 'em_conferencia':
      return 'em conferência';
    case 'nao_aplicavel':
      return 'não aplicável';
    case 'vencido':
      return 'vencido';
    case 'ilegivel':
      return 'ilegível';
    case 'duplicado':
      return 'duplicado';
    default:
      return status.replace(/_/g, ' ');
  }
}

function checklistTone(status: string): 'ok' | 'warn' | 'danger' | 'neutral' | 'info' {
  switch (status) {
    case 'recebido':
      return 'ok';
    case 'pendente':
      return 'warn';
    case 'em_conferencia':
      return 'info';
    case 'vencido':
    case 'ilegivel':
      return 'danger';
    default:
      return 'neutral';
  }
}

function checklistRows(c: Record<string, unknown>): Array<Record<string, unknown>> {
  if (Array.isArray(c.checklist_resumo)) return c.checklist_resumo as Array<Record<string, unknown>>;
  return [];
}

function aggregateChecklistStatus(
  rows: Array<Record<string, unknown>>,
  matcher: (row: Record<string, unknown>) => boolean,
): string | null {
  const matches = rows.filter(matcher).map((row) => String(row.status || '').trim()).filter(Boolean);
  if (!matches.length) return null;
  if (matches.some((status) => status === 'vencido')) return 'vencido';
  if (matches.some((status) => status === 'ilegivel')) return 'ilegivel';
  if (matches.some((status) => status === 'pendente')) return 'pendente';
  if (matches.some((status) => status === 'em_conferencia')) return 'em_conferencia';
  if (matches.some((status) => status === 'duplicado')) return 'duplicado';
  if (matches.every((status) => status === 'nao_aplicavel')) return 'nao_aplicavel';
  if (matches.every((status) => status === 'recebido')) return 'recebido';
  return matches[0] || null;
}

function resumoCasoTriagem(finalidade: string | null, objeto: string | null, sugestao: string | null, faseTriagem: string): string {
  const objetoCurto = (objeto || '').toLowerCase().includes('pulverizador')
    ? 'pulverizador'
    : (objeto ? objeto.toLowerCase() : 'objeto em definição');
  return [
    `${finalidade ?? 'Operação'} rural para aquisição de ${objetoCurto}.`,
    `Fase atual: ${faseTriagem}.`,
    sugestao ? `${sugestao} consta apenas como instituição sugerida.` : null,
  ].filter(Boolean).join(' ');
}

export const creditoCardFieldMap: CardFieldMap = (card) => {
  const cliente = String(card.cliente_nome ?? '—');
  const instituicao = card.instituicao_nome
    ? String(card.instituicao_nome)
    : card.instituicao
      ? String(card.instituicao)
      : null;
  const programa = normalizeProgramaLinha(card.programa_nome) ?? normalizeProgramaLinha(card.programa);
  const finalidade = fmtFinalidade(card.finalidade_codigo ?? card.finalidade);
  const valor = fmtBRLOrNull(card.valor_solicitado);
  const cultura = card.cultura_atividade ? String(card.cultura_atividade) : null;
  const statusRaw = card.status ? String(card.status) : null;
  const statusVal = statusLabel(statusRaw);
  const tempoCard = formatCardTime(card);
  const isTriagem = String(card.tipo_processo || '') === 'triagem_credito';
  const isComprovacoes = String(card.tipo_processo || '') === 'comprovacoes';
  const faseAtual = String(card.fase_nome ?? card.kanban_coluna_nome ?? '');
  const dataPrevistaLiberacao = fmtDate(card.data_prevista_liberacao);
  const dataPrevistaLaudo = fmtDate(card.data_prevista_envio_laudo);
  const valorProgramado = fmtBRLOrNull(card.valor_liberacao_programada);
  const valorTotal = Number(card.valor_aprovado);
  const valorResidual = Number(card.valor_liberacao_programada);
  const percentualResidual = Number.isFinite(valorTotal) && valorTotal > 0
    && Number.isFinite(valorResidual) && valorResidual >= 0
    ? (valorResidual / valorTotal * 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : null;
  const faseTriagem = fmtFaseTriagem(card.fase_nome ?? card.status_triagem)
    ?? (statusRaw === 'em_analise' ? 'Análise' : statusVal);
  const assistente = card.responsavel_nome ? String(card.responsavel_nome) : null;

  if (isTriagem) {
    return {
      title: cliente,
      subtitle: [valor, cultura].filter(Boolean).join(' | ') || null,
      logo: instituicao ? createElement(InstituicaoLogo, { nome: instituicao, size: 30 }) : null,
      chips: [
        ...(faseTriagem
          ? [
              {
                label: '',
                value: [faseTriagem, tempoCard].filter(Boolean).join(' · '),
                tone: 'info' as const,
              },
            ]
          : []),
        ...(finalidade ? [{ label: '', value: finalidade, tone: 'neutral' as const }] : []),
        ...(assistente ? [{ label: '', value: assistente, tone: 'neutral' as const }] : []),
      ],
      meta: null,
    };
  }

  return {
    title: cliente,
    subtitle: valor ? `${valor}${cultura ? ` · ${cultura}` : ''}` : cultura,
    // A instituição vira badge de logo no topo-direito (substitui o chip de texto).
    logo: instituicao ? createElement(InstituicaoLogo, { nome: instituicao, size: 30 }) : null,
    chips: [
      {
        label: '',
        value: [statusVal, tempoCard].filter(Boolean).join(' · '),
        tone: statusTone(statusRaw),
      },
      { label: 'Programa', value: programa, tone: 'accent' },
      { label: 'Finalidade', value: finalidade, tone: 'neutral' },
      ...(isComprovacoes && faseAtual === 'Liberação programada' && dataPrevistaLiberacao
        ? [{ label: 'Liberação prevista', value: dataPrevistaLiberacao, tone: 'warn' as const }]
        : []),
    ],
    meta: isComprovacoes
      ? [
          dataPrevistaLaudo ? `Laudo: ${dataPrevistaLaudo}` : null,
          valorProgramado
            ? `Parcela: ${valorProgramado}${percentualResidual ? ` (${percentualResidual}% residual)` : ''}`
            : null,
          card.proxima_acao ? String(card.proxima_acao) : null,
        ].filter(Boolean).join(' · ') || null
      : card.proxima_acao ? String(card.proxima_acao) : null,
  };
};

// Helper pra ler campos joinados do cliente/imóvel sem quebrar quando ausentes.
function joined(c: Record<string, unknown>, k: string): string | null {
  const v = c[k];
  if (v == null || v === '') return null;
  return String(v);
}

function fmtArea(v: unknown): string | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + ' ha';
}

export const creditoDetailFieldMap: DetailFieldMap = (card) => {
  const c = card as Record<string, unknown>;
  const out: ReturnType<DetailFieldMap> = [];
  const isTriagem = c.tipo_processo === 'triagem_credito';
  const pendenciasCadastrais = buildPendenciasCadastrais(c);
  const sugestao = fmtSugestaoInstituicao(c.instituicao_sugerida ?? c.instituicao_nome);
  const finalidade = fmtFinalidade(c.finalidade_codigo);
  const objeto = joined(c, 'cultura_atividade');
  const faseTriagem = fmtFaseTriagem(c.status_triagem) ?? 'Análise';
  const resumoCaso = resumoCasoTriagem(finalidade, objeto, sugestao, faseTriagem);

  if (isTriagem) {
    const proximaAcao = String(
      c.proxima_acao || 'Confirmar banco de interesse, responsável pela operação e documentos rurais disponíveis.',
    );
    const checklist = checklistRows(c);
    const hasImovelBeneficiado = !!c.imovel_id;
    const statusMatricula = hasImovelBeneficiado
      ? (joined(c, 'imovel_matricula') ? 'recebido' : 'pendente')
      : 'pendente';
    const statusCar = hasImovelBeneficiado
      ? (aggregateChecklistStatus(checklist, (row) => /car recebido/i.test(String(row.nome_documento || ''))) || 'pendente')
      : 'pendente';
    const statusCcir = hasImovelBeneficiado
      ? (aggregateChecklistStatus(checklist, (row) => /ccir recebido/i.test(String(row.nome_documento || ''))) || 'pendente')
      : 'pendente';
    const statusItr = hasImovelBeneficiado
      ? (aggregateChecklistStatus(checklist, (row) => /itr recebido/i.test(String(row.nome_documento || ''))) || 'pendente')
      : 'pendente';

    return [
      {
        label: 'Resumo consolidado',
        value: resumoCaso,
        section: 'Resumo do caso',
        tab: 'cadastro',
        alwaysShow: true,
      },
      {
        label: 'CPF',
        value: joined(c, 'cliente_cpf') ?? '—',
        section: 'Cliente',
        tab: 'cadastro',
        alwaysShow: true,
      },
      {
        label: 'Município/UF',
        value: [joined(c, 'cliente_municipio'), joined(c, 'cliente_uf')].filter(Boolean).join('/') || '—',
        section: 'Cliente',
        tab: 'cadastro',
        alwaysShow: true,
      },
      ...(pendenciasCadastrais.length > 0 ? [{
        label: 'Pendências',
        value: pendenciasCadastrais.join('\n'),
        section: 'Pendências cadastrais',
        tab: 'cadastro',
        alwaysShow: true,
      }] : []),
      {
        label: 'Status',
        value: hasImovelBeneficiado ? 'em conferência' : 'pendente de vínculo ou conferência',
        section: 'Imóvel beneficiado',
        tab: 'cadastro',
        alwaysShow: true,
      },
      {
        label: 'Matrícula',
        value: checklistLabel(statusMatricula),
        section: 'Imóvel beneficiado',
        tab: 'cadastro',
        chipTone: checklistTone(statusMatricula),
        alwaysShow: true,
      },
      {
        label: 'CCIR',
        value: checklistLabel(statusCcir),
        section: 'Imóvel beneficiado',
        tab: 'cadastro',
        chipTone: checklistTone(statusCcir),
        alwaysShow: true,
      },
      {
        label: 'CAR',
        value: checklistLabel(statusCar),
        section: 'Imóvel beneficiado',
        tab: 'cadastro',
        chipTone: checklistTone(statusCar),
        alwaysShow: true,
      },
      {
        label: 'ITR',
        value: checklistLabel(statusItr),
        section: 'Imóvel beneficiado',
        tab: 'cadastro',
        chipTone: checklistTone(statusItr),
        alwaysShow: true,
      },
      {
        label: 'Fase atual',
        value: `2. ${faseTriagem}`,
        section: 'Fase atual',
        tab: 'cadastro',
        alwaysShow: true,
      },
      {
        label: 'Objetivo',
        value: 'Triagem documental e operacional.',
        section: 'Fase atual',
        tab: 'cadastro',
        alwaysShow: true,
      },
      {
        label: 'Regra da triagem',
        value: 'A triagem gera apontamentos para orientar a definição final, sem travar o avanço do CARD Pai.',
        section: 'Fase atual',
        tab: 'cadastro',
        alwaysShow: true,
      },
      {
        label: 'Instituição financeira',
        value: joined(c, 'instituicao_nome') ?? joined(c, 'instituicao_definitiva') ?? '—',
        section: 'Definições obrigatórias',
        tab: 'cadastro',
        alwaysShow: true,
        field: 'instituicao_id',
        type: 'select',
        rawValue: asRaw(c.instituicao_id),
        options: TRIAGEM_INSTITUICOES,
      },
      {
        label: 'Assistente da operação',
        value: joined(c, 'responsavel_nome') ?? '—',
        section: 'Definições obrigatórias',
        tab: 'cadastro',
        alwaysShow: true,
        type: 'triagem_aceite',
        triagemAssistenteNome: joined(c, 'responsavel_nome'),
        triagemAssistenteStatus: String(c.responsavel_travado || '').trim() ? 'aceito' : 'pendente',
      },
      {
        label: 'Finalidade',
        value: finalidade ?? '—',
        section: 'Definições obrigatórias',
        tab: 'cadastro',
        alwaysShow: true,
        field: 'finalidade_codigo',
        type: 'select',
        rawValue: asRaw(c.finalidade_codigo),
        options: FINALIDADE,
      },
      {
        label: 'Próxima ação',
        value: proximaAcao,
        section: 'Definições obrigatórias',
        tab: 'cadastro',
        alwaysShow: true,
        field: 'proxima_acao',
        type: 'text',
        rawValue: asRaw(c.proxima_acao),
      },
      {
        label: 'Prazo da próxima ação',
        value: fmtDate(c.prazo_proxima_acao) ?? '—',
        section: 'Definições obrigatórias',
        tab: 'cadastro',
        alwaysShow: true,
        field: 'prazo_proxima_acao',
        type: 'date',
        rawValue: asRaw(c.prazo_proxima_acao),
      },
    ];
  }

  // ── Aba Cadastro: dados read-only do cliente + imóvel (vêm do JOIN no GET).
  // Edição é feita na ficha do cliente, não aqui.
  out.push(
    {
      label: 'Nome',
      value: joined(c, 'cliente_nome') ?? '—',
      section: 'Cliente',
      tab: 'cadastro',
      alwaysShow: true,
    },
    {
      label: 'CPF',
      value: joined(c, 'cliente_cpf') ?? '—',
      section: 'Cliente',
      tab: 'cadastro',
      alwaysShow: true,
    },
    {
      label: 'Município/UF',
      value:
        [joined(c, 'cliente_municipio'), joined(c, 'cliente_uf')]
          .filter(Boolean)
          .join('/') || '—',
      section: 'Cliente',
      tab: 'cadastro',
      alwaysShow: true,
    },
    {
      label: 'Renda bruta anual',
      value: fmtBRLOrNull(c.cliente_renda_bruta_anual) ?? '—',
      section: 'Cliente',
      tab: 'cadastro',
    },
    {
      label: 'Denominação',
      value: joined(c, 'imovel_denominacao') ?? '—',
      section: 'Imóvel beneficiado',
      tab: 'cadastro',
    },
    {
      label: 'Matrícula',
      value: joined(c, 'imovel_matricula') ?? '—',
      section: 'Imóvel beneficiado',
      tab: 'cadastro',
    },
    {
      label: 'Área total',
      value: fmtArea(c.imovel_area_total_ha) ?? '—',
      section: 'Imóvel beneficiado',
      tab: 'cadastro',
    },
    {
      label: 'CCIR',
      value:
        joined(c, 'imovel_ccir_numero')
          ? `${joined(c, 'imovel_ccir_numero')}${joined(c, 'imovel_ccir_status') ? ' · ' + joined(c, 'imovel_ccir_status') : ''}`
          : '—',
      section: 'Imóvel beneficiado',
      tab: 'cadastro',
    },
  );

  // ── Aba Operação: gestão do processo + fases do pipe FNO + observações.
  // Não inclui buildDadosPrincipais — CPF/Imóvel/Matrícula ficam só em Cadastro
  // pra evitar duplicação. "Gestão" mantém só campos operacionais.
  const operacao: ReturnType<DetailFieldMap> = [];

  // Responsável e Previsão de conclusão entram no fim da seção Cliente
  // (não tem mais "Gestão" como seção separada).
  const responsaveisList = parseResponsaveis(c.responsaveis);
  out.push(
    {
      label: 'Responsável(is)',
      value: '',
      section: 'Cliente',
      tab: 'cadastro',
      type: 'responsaveis',
      responsaveis: responsaveisList,
    },
    {
      label: 'Previsão de conclusão',
      value: fmtDate(c.data_previsao_conclusao) ?? '—',
      section: 'Cliente',
      tab: 'cadastro',
      field: 'data_previsao_conclusao',
      type: 'date',
      rawValue: asRaw(c.data_previsao_conclusao),
    },
  );
  if (asStr(c.cartorio)) {
    out.push({
      label: 'Cartório',
      value: String(c.cartorio),
      section: 'Imóvel beneficiado',
      tab: 'cadastro',
      alwaysShow: true,
    });
  }

  if (isTriagem && pendenciasCadastrais.length > 0) {
    out.push({
      label: 'Pendências',
      value: pendenciasCadastrais.join('\n'),
      section: 'Pendências cadastrais',
      tab: 'cadastro',
      alwaysShow: true,
    });
  }

  const pipeSpec = c.tipo_processo === 'triagem_credito'
    ? PIPE_TRIAGEM_CREDITO
    : c.tipo_processo === 'comprovacoes'
      ? PIPE_COMPROVACOES
    : c.tipo_processo === 'sicoob_fronteiras'
      ? PIPE_SICOOB_FRONTEIRAS
    : c.tipo_processo === 'sicredi'
      ? PIPE_SICREDI
    : c.tipo_processo === 'bradesco'
      ? PIPE_BRADESCO
    : c.tipo_processo === 'credisis'
      ? PIPE_CREDISIS
      : PIPE_CREDITO;
  operacao.push(...buildPhases(c, pipeSpec));
  operacao.push(...buildObservacoes(c));

  // Tudo da operação vai pra aba Cadastro (consolidação pedida pelo time):
  // Cliente → Imóvel → Gestão → Fase atual → Fases anteriores → Observações.
  for (const f of operacao) {
    out.push({ ...f, tab: 'cadastro' });
  }

  return out;
};
